import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Camera, Search, ScanLine, Trash2, RotateCw, Crop, Undo2, Loader2, Save, X, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { artSrc, readImageFile, rotateImage, uploadArtwork } from "@/components/riptag/util";
import { ImageCropModal } from "@/components/ImageCropModal";
import { ArtworkSearchModal } from "@/components/riptag/ArtworkSearchModal";
import { CdScanModal } from "@/components/riptag/CdScanModal";
import { appConfirm } from "@/components/AppDialogHost";

export const ArtworkPanel = ({ track, onChanged }) => {
  const [pending, setPending] = useState(null);
  const [dims, setDims] = useState(null);
  const [cropFile, setCropFile] = useState(null);
  const [scope, setScope] = useState("track");
  const [busy, setBusy] = useState("");
  const [modal, setModal] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  const hasAlbum = !!track.album_id;
  const targetId = scope === "album" && hasAlbum ? track.album_id : track.track_id;
  const effScope = scope === "album" && hasAlbum ? "album" : "track";

  const pick = async (file) => {
    if (!file) return;
    try {
      setPending(await readImageFile(file));
    } catch (e) { toast.error(e.message); }
  };

  const save = async () => {
    setBusy("save");
    try {
      const up = await uploadArtwork(api, pending, "cover");
      const r = await api.post("/rip/artwork/apply", { scope: effScope, id: targetId, art_id: up.art_id });
      toast.success(effScope === "album" ? `Artwork applied to ${r.data.affected} album tracks` : "Artwork saved to this track");
      setPending(null);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Artwork save failed"); }
    setBusy("");
  };

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try { await fn(); if (okMsg) toast.success(okMsg); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setBusy("");
  };

  const remove = async () => {
    if (!await appConfirm(effScope === "album" ? "Remove artwork from every track in this album?" : "Remove this track's artwork?")) return;
    act("remove", () => api.post("/rip/artwork/remove", { scope: effScope, id: targetId }), "Artwork removed");
  };

  const restore = () => act("restore",
    () => api.post("/rip/artwork/restore", { scope: effScope, id: targetId }), "Original artwork restored");

  const btn = "inline-flex items-center gap-1 rounded-md border border-line px-2.5 py-2 sm:px-2 sm:py-1 text-[10px] font-bold hover:border-brand-amber/60 transition disabled:opacity-50";

  return (
    <div className="rounded-lg border border-line bg-bg-elevated/40 p-3 space-y-2" data-testid="rip-art-panel">
      <div className="flex flex-wrap items-center justify-between gap-1.5">
        <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Album Artwork</div>
        {hasAlbum && (
          <div className="flex gap-1 text-[9px] font-mono uppercase">
            {[["track", "This track"], ["album", "Whole album"]].map(([v, l]) => (
              <button key={v} onClick={() => setScope(v)} data-testid={`rip-art-scope-${v}`}
                className={`rounded-full px-2 py-0.5 border transition ${scope === v ? "bg-brand-amber text-black border-brand-amber font-bold" : "border-line text-ink-muted hover:text-ink-primary"}`}>
                {l}
              </button>
            ))}
          </div>
        )}
      </div>

      {pending ? (
        <div className="space-y-2" data-testid="rip-art-pending">
          <img src={pending} alt="New artwork preview" data-testid="rip-art-pending-preview"
            onLoad={(e) => setDims({ w: e.target.naturalWidth, h: e.target.naturalHeight })}
            className="max-w-[200px] max-h-44 w-auto h-auto rounded-md object-contain border border-brand-amber/60 bg-black/30" />
          {dims && (
            <div className="text-[10px] font-mono text-ink-muted" data-testid="rip-art-pending-dims">
              {dims.w}×{dims.h}px{dims.w === dims.h ? " · square ✓" : ""}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            <button onClick={async () => { try { setPending(await rotateImage(pending)); } catch (e) { toast.error(e.message); } }} className={btn} data-testid="rip-art-rotate-btn">
              <RotateCw className="h-3 w-3" /> Rotate
            </button>
            <button onClick={async () => { try { setCropFile(await (await fetch(pending)).blob()); } catch { toast.error("Could not open the crop tool"); } }}
              className={btn} data-testid="rip-art-crop-btn">
              <Crop className="h-3 w-3" /> Crop
            </button>
            <button onClick={save} disabled={!!busy} data-testid="rip-art-save-btn"
              className="inline-flex items-center gap-1 rounded-md bg-brand-amber text-black px-2.5 py-1 text-[10px] font-bold hover:opacity-90 transition disabled:opacity-50">
              {busy === "save" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save to {effScope === "album" ? "album" : "track"}
            </button>
            <button onClick={() => setPending(null)} className={btn} data-testid="rip-art-cancel-btn">
              <X className="h-3 w-3" /> Cancel
            </button>
          </div>
          <div className="text-[10px] text-ink-muted">Review the image, then Save — nothing is changed until you do.</div>
        </div>
      ) : (
        <div className="flex items-start gap-3">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pick(e.dataTransfer.files?.[0]); }}
            onClick={() => fileRef.current?.click()}
            data-testid="rip-art-dropzone"
            className={`relative w-28 h-28 shrink-0 rounded-md border cursor-pointer overflow-hidden flex items-center justify-center transition ${
              dragOver ? "border-brand-amber bg-brand-amber/10" : "border-line bg-bg-surface hover:border-brand-amber/50"}`}>
            {track.art_url ? (
              <img src={artSrc(track.art_url)} alt="Album art" data-testid="rip-art-preview"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
                className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="text-center text-ink-muted px-1">
                <ImageIcon className="h-5 w-5 mx-auto mb-1 opacity-50" />
                <div className="text-[9px] font-mono leading-tight">Drop image or click to browse</div>
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => fileRef.current?.click()} className={btn} data-testid="rip-art-browse-btn">
                <ImagePlus className="h-3 w-3" /> {track.art_url ? "Replace" : "Upload"}
              </button>
              <button onClick={() => camRef.current?.click()} className={btn} data-testid="rip-art-camera-btn">
                <Camera className="h-3 w-3" /> Camera
              </button>
              <button onClick={() => setModal("search")} className={btn} data-testid="rip-art-find-btn">
                <Search className="h-3 w-3" /> Find Artwork
              </button>
              <button onClick={() => setModal("scan")} data-testid="rip-art-scan-btn"
                className="inline-flex items-center gap-1 rounded-md border border-brand-cyan/50 text-brand-cyan px-2.5 py-2 sm:px-2 sm:py-1 text-[10px] font-bold hover:bg-brand-cyan/10 transition">
                <ScanLine className="h-3 w-3" /> Scan CD
              </button>
              {track.art_url && (
                <button onClick={remove} disabled={!!busy} className={`${btn} hover:border-red-400/60 hover:text-red-400`} data-testid="rip-art-remove-btn">
                  <Trash2 className="h-3 w-3" /> Remove
                </button>
              )}
              <button onClick={restore} disabled={!!busy || !track.orig_meta} className={btn} data-testid="rip-art-restore-btn"
                title={track.orig_meta ? "Restore the artwork this track originally had" : "No original snapshot yet — nothing to restore"}>
                <Undo2 className="h-3 w-3" /> Restore
              </button>
            </div>
            <div className="text-[10px] text-ink-muted leading-relaxed">
              JPG, PNG or WebP · max 10MB. Phone photos welcome.
              {track.art_url && (
                <div className="mt-0.5" data-testid="rip-art-status">
                  {track.art_embedded ? "Embedded into the file." : "Embedded when you Write Tags."}
                  {track.art_attribution ? ` · ${track.art_attribution}` : ""}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/*" className="hidden"
        data-testid="rip-art-upload-input" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden"
        data-testid="rip-art-camera-input" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ""; }} />

      {cropFile && (
        <ImageCropModal
          file={cropFile}
          aspect={1}
          title="Crop album artwork"
          maxWidth={1400}
          onCancel={() => setCropFile(null)}
          onCropped={(blob) => {
            const r = new FileReader();
            r.onload = () => { setPending(r.result); setCropFile(null); };
            r.readAsDataURL(blob);
          }}
        />
      )}

      {modal === "search" && (
        <ArtworkSearchModal track={track} onClose={() => setModal("")}
          onApplied={() => { setModal(""); onChanged(); }} />
      )}
      {modal === "scan" && (
        <CdScanModal track={track} onClose={() => setModal("")}
          onApplied={() => { setModal(""); onChanged(); }} />
      )}
    </div>
  );
};
